/**
 * ADR-0222 (v1) + ADR-0224 (v2) — Business skills.
 *
 * Seven built-in link-returning skills a site can enable with one URL each
 * (and, for `get_pricing`, an optional merchant-maintained plan list). The
 * merchant types the URL (and an optional short label); the edge answers
 * the agent's `tools/call` from the published SITE_SKILLS KV value — no
 * origin round-trip, no connector dispatch, no PII capture.
 *
 * This module is pure (shared between edge + origin + dashboard):
 *   - the closed set of skill_ids (append-only — reordering flips signed
 *     bytes on every site that has >1 business skill enabled),
 *   - the per-skill default `name` + `description` (ride into the SIGNED
 *     `AgentCardCapability` row verbatim — stable order below is
 *     signature-adjacent, do not reorder),
 *   - the booking-host allow-list for `book_meeting`,
 *   - the status-host allow-list for `get_status`,
 *   - the trust-center host hint list for `get_trust_center` (non-authoritative
 *     — the trust slot warn-doesn't-block on off-list hosts),
 *   - the plan interface + matcher the edge dispatch uses for optional
 *     `plan` input on `get_pricing` and `sign_up`.
 */
/** Closed set of business-skill ids (reserved globally; a custom skill
 *  create with any of these ids is rejected by the origin as
 *  `skill_id_reserved`).
 *
 *  **Append-only invariant** — new ids are added at the end. The KV
 *  publisher iterates this array to append capabilities to the signed
 *  card in a deterministic order; reordering silently changes the
 *  signed bytes on every site that has >1 business skill enabled. */
export const BUSINESS_SKILL_IDS = [
    // v1 — ADR-0222
    'get_pricing',
    'request_demo',
    'sign_up',
    'book_meeting',
    // v2 — ADR-0224. APPEND ONLY — never reorder.
    'contact_support',
    'get_status',
    'get_trust_center',
];
/** O(1) membership for the reserved ids. */
export const BUSINESS_SKILL_ID_SET = new Set(BUSINESS_SKILL_IDS);
/** True iff `id` is one of the reserved business-skill ids. */
export function isBusinessSkillId(id) {
    return BUSINESS_SKILL_ID_SET.has(id);
}
export const BUSINESS_SKILL_DEFAULTS = [
    {
        skill_id: 'get_pricing',
        name: 'Get pricing',
        description: 'Return the pricing page URL and the list of plans. Optional `plan` input matches by name (case-insensitive); match returns that plan with its sign-up URL.',
    },
    {
        skill_id: 'request_demo',
        name: 'Request a demo',
        description: 'Return the demo request URL for this site.',
    },
    {
        skill_id: 'sign_up',
        name: 'Sign up',
        description: 'Return the sign-up or free-trial URL. Optional `plan` input returns that plan’s sign-up URL when set.',
    },
    {
        skill_id: 'book_meeting',
        name: 'Book a meeting',
        description: 'Return the meeting booking URL for this site.',
    },
    // v2 — ADR-0224.
    {
        skill_id: 'contact_support',
        name: 'Contact support',
        description: 'Return the support or contact page URL for this site.',
    },
    {
        skill_id: 'get_status',
        name: 'Get status',
        description: 'Return the status page URL for this site.',
    },
    {
        skill_id: 'get_trust_center',
        name: 'Get trust center',
        description: 'Return the trust, security, or compliance page URL for this site.',
    },
];
/** Lookup the default for a given business-skill id. Returns `undefined`
 *  for a non-business skill id; callers that already know the id is a
 *  business skill (via {@link isBusinessSkillId}) can safely `!`. */
export function getBusinessSkillDefault(skill_id) {
    return BUSINESS_SKILL_DEFAULTS.find((b) => b.skill_id === skill_id);
}
/* ================================================================== */
/* Booking host allow-list (book_meeting)                             */
/* ================================================================== */
/**
 * Closed allow-list of hosts the `book_meeting` URL may point at. The
 * only accepted protocol is `https:`; hosts match the full hostname
 * exactly OR as a subdomain of one of the base hosts (an eTLD-aware
 * check would be nice-to-have but these are closed, well-known
 * eTLDs — exact + "ends with .{host}" is sufficient).
 *
 * The `calendar.google.com` entry additionally requires the pathname
 * to start with `/calendar/appointments/` — bare `calendar.google.com`
 * URLs are overwhelmingly non-booking (my-calendar, shared events) so
 * accepting them would surface junk links.
 */
export const BOOKING_HOST_ALLOW_LIST = [
    'calendly.com',
    'cal.com',
    'meetings.hubspot.com',
    'savvycal.com',
    'calendar.app.google',
    'calendar.google.com',
    'tidycal.com',
    'zcal.co',
];
/** Hosts in the allow-list that require a specific pathname prefix. */
const BOOKING_HOST_PATH_PREFIX = {
    'calendar.google.com': '/calendar/appointments/',
};
/**
 * True iff `url` is an `https:` URL whose host is on the booking-host
 * allow-list AND (for hosts in {@link BOOKING_HOST_PATH_PREFIX}) whose
 * pathname begins with the required prefix.
 */
export function isBookingHostUrl(url) {
    let parsed;
    try {
        parsed = new URL(url);
    }
    catch {
        return false;
    }
    if (parsed.protocol !== 'https:')
        return false;
    const host = parsed.hostname.toLowerCase();
    const hit = BOOKING_HOST_ALLOW_LIST.find((base) => host === base || host.endsWith(`.${base}`));
    if (hit === undefined)
        return false;
    const prefix = BOOKING_HOST_PATH_PREFIX[hit];
    if (prefix !== undefined && !parsed.pathname.startsWith(prefix)) {
        return false;
    }
    return true;
}
/* ================================================================== */
/* Status host allow-list (get_status)                                */
/* ================================================================== */
/**
 * ADR-0224 — closed allow-list of hosts a `get_status` URL may point
 * at (in addition to the merchant's own domain / subdomain). Status
 * pages are a trust / incident surface, so the gate is **strict** — a
 * tweet-storm or Google Doc linked as "status" is noise; the origin
 * validator rejects off-list, off-domain `get_status` URLs with
 * `url_not_a_status_host`. The list is reviewed in ADR-0224 and
 * expanded in a follow-up when real merchants bring hosts we missed.
 *
 * HTTPS only; exact match or `.endsWith('.<host>')` subdomain match.
 */
export const STATUS_HOST_ALLOW_LIST = [
    'statuspage.io',
    'status.io',
    'instatus.com',
    'betteruptime.com',
    'statuspal.io',
];
/** True iff `url` is an `https:` URL whose host matches the status-host
 *  allow-list exactly or as a subdomain of one of its entries. Does NOT
 *  accept the merchant's own domain — that branch lives in the origin
 *  validator which knows the site domain. */
export function isStatusHostUrl(url) {
    let parsed;
    try {
        parsed = new URL(url);
    }
    catch {
        return false;
    }
    if (parsed.protocol !== 'https:')
        return false;
    const host = parsed.hostname.toLowerCase();
    return STATUS_HOST_ALLOW_LIST.some((base) => host === base || host.endsWith(`.${base}`));
}
/* ================================================================== */
/* Trust-center host hint list (get_trust_center)                     */
/* ================================================================== */
/**
 * ADR-0224 — hint list of hosts a `get_trust_center` URL often points
 * at. **Non-authoritative** — the trust slot warns-doesn't-block on
 * off-list hosts (same posture as the pricing / demo / signup slots).
 * `trust.*` subdomains are a common merchant pattern; the matcher
 * accepts any `host.startsWith('trust.')`.
 *
 * HTTPS only; exact match or `.endsWith('.<host>')` subdomain match
 * (plus the `trust.*` prefix branch).
 */
export const TRUST_CENTER_HOST_HINT_LIST = [
    'safebase.io',
    'trust.vanta.com',
    'trust.drata.com',
    'conveyor.com',
];
/** True iff `url` is an `https:` URL whose host is on
 *  {@link TRUST_CENTER_HOST_HINT_LIST} OR starts with `trust.`. The
 *  origin validator uses this to drop the off-domain warning on
 *  recognised trust hosts; off-list, off-domain URLs still save with
 *  a warning (not an error). */
export function isTrustCenterHostUrl(url) {
    let parsed;
    try {
        parsed = new URL(url);
    }
    catch {
        return false;
    }
    if (parsed.protocol !== 'https:')
        return false;
    const host = parsed.hostname.toLowerCase();
    if (host.startsWith('trust.'))
        return true;
    return TRUST_CENTER_HOST_HINT_LIST.some((base) => host === base || host.endsWith(`.${base}`));
}
/* ================================================================== */
/* Plans (get_pricing + sign_up)                                      */
/* ================================================================== */
/** Max plans per site. Enforced server-side on PUT; defense-in-depth
 *  cap in the edge publish validator. */
export const BUSINESS_PLAN_MAX = 10;
/** Max name length (chars). Signed-adjacent (lands in KV); strip
 *  control chars at the save boundary. */
export const BUSINESS_PLAN_NAME_MAX = 80;
/** Max description length. */
export const BUSINESS_PLAN_DESCRIPTION_MAX = 140;
/** Closed enum: billing period for a plan. */
export const BUSINESS_PLAN_PERIODS = [
    'month',
    'year',
    'one_time',
    'custom',
];
const AMOUNT_DECIMAL_RE = /^[0-9]{1,10}(\.[0-9]{1,2})?$/;
const CURRENCY_RE = /^[A-Z]{3}$/;
/** True iff `v` is a non-empty decimal string with ≤10 integer digits
 *  and 0–2 fractional digits. */
export function isValidPlanAmountDecimal(v) {
    return typeof v === 'string' && v.length > 0 && AMOUNT_DECIMAL_RE.test(v);
}
/** True iff `v` is a 3-letter ISO-4217 upper-case currency. */
export function isValidPlanCurrency(v) {
    return typeof v === 'string' && CURRENCY_RE.test(v);
}
/**
 * Case-insensitive plan matcher. Returns the first matching plan or
 * `undefined`. Deterministic across three fallback passes:
 *
 *   1. exact (`a.name.toLowerCase() === q.toLowerCase()`)
 *   2. prefix (`a.name.toLowerCase().startsWith(q)`)
 *   3. contains (`a.name.toLowerCase().includes(q)`)
 *
 * Each pass is in array order (merchant-authored, so the preferred-plan
 * ordering is honoured). An empty / whitespace query returns `undefined`.
 */
export function matchPlan(plans, query) {
    if (plans === undefined || plans.length === 0)
        return undefined;
    if (typeof query !== 'string')
        return undefined;
    const q = query.trim().toLowerCase();
    if (q.length === 0)
        return undefined;
    const named = plans.map((p) => ({ p, name: p.name.toLowerCase() }));
    for (const { p, name } of named)
        if (name === q)
            return p;
    for (const { p, name } of named)
        if (name.startsWith(q))
            return p;
    for (const { p, name } of named)
        if (name.includes(q))
            return p;
    return undefined;
}
/* ================================================================== */
/* Business pages found by the homepage scan (ADR-0226)               */
/* ================================================================== */
/**
 * ADR-0226 — the closed subset of homepage `key_pages` kinds that map
 * to a business skill. Order follows {@link BUSINESS_SKILL_IDS} (the
 * order every surface lists them in). The homepage probe's other kinds
 * (`about`, `faq`, `shipping`, …) are NOT business pages.
 */
export const BUSINESS_PAGE_KINDS = [
    'pricing',
    'demo',
    'signup',
    'booking',
    'contact',
    'status',
    'trust',
];
const BUSINESS_PAGE_KIND_SET = new Set(BUSINESS_PAGE_KINDS);
/** True iff `kind` is one of the closed business-page kinds. */
export function isBusinessPageKind(kind) {
    return typeof kind === 'string' && BUSINESS_PAGE_KIND_SET.has(kind);
}
/**
 * ADR-0226 — key-page kind → business skill id. Same mapping as the
 * origin's `suggested_url` projection (`sites-business-skills.ts`) and
 * the readiness `detected_business_links` suggestion (ADR-0222 §5,
 * ADR-0224 §10).
 */
export const BUSINESS_PAGE_KIND_TO_SKILL_ID = {
    pricing: 'get_pricing',
    demo: 'request_demo',
    signup: 'sign_up',
    booking: 'book_meeting',
    contact: 'contact_support',
    status: 'get_status',
    trust: 'get_trust_center',
};
/**
 * Normalise an untrusted list (bare kind strings OR `{ kind }` objects,
 * e.g. a scan's `key_pages`) to the closed, de-duplicated kind list in
 * canonical order. Out-of-enum entries are dropped. Never throws; only
 * ever returns closed enum members, so the result is safe for a public
 * wire, an email, or a log line. No URL or page text survives.
 */
export function businessPageKindsFrom(raw) {
    if (!Array.isArray(raw))
        return [];
    const seen = new Set();
    // Bounded: the homepage probe caps key pages far below this; the cap
    // is defense-in-depth against a hand-built payload.
    for (const item of raw.slice(0, 64)) {
        const kind = typeof item === 'string'
            ? item
            : item !== null && typeof item === 'object'
                ? item.kind
                : undefined;
        if (isBusinessPageKind(kind))
            seen.add(kind);
    }
    return BUSINESS_PAGE_KINDS.filter((k) => seen.has(k));
}
/** Short lower-case label per kind for the one-line scan summary. */
export const BUSINESS_PAGE_LABELS = {
    pricing: 'pricing',
    demo: 'book a demo',
    signup: 'sign up',
    booking: 'book a meeting',
    contact: 'contact support',
    status: 'status page',
    trust: 'trust center',
};
/**
 * ADR-0226 — the one informational line the sill.so scan card, the
 * hosted report and the readiness-report email show, e.g.
 * `Agents could answer for you: pricing · book a demo · status page.`
 * Returns `null` when nothing was found — the line is then omitted
 * entirely (no "not found" tone). Built ONLY from closed kinds, so no
 * third-party text can reach the rendered line.
 */
export function businessPagesLine(kinds) {
    const closed = businessPageKindsFrom(kinds);
    if (closed.length === 0)
        return null;
    return `Agents could answer for you: ${closed.map((k) => BUSINESS_PAGE_LABELS[k]).join(' · ')}.`;
}
//# sourceMappingURL=business-skills.js.map